/**
 * Form Field Wrapper Component
 *
 * Wraps any form field with:
 * - Label with optional required indicator
 * - Error message display
 * - Consistent styling
 *
 * Eliminates the need for repetitive label/error code blocks.
 *
 * Usage:
 * <FormFieldWrapper label="Email" required error={errors.email?.message}>
 *   <Input {...field} />
 * </FormFieldWrapper>
 */

import React from 'react';
import { Typography } from 'antd';
import { useWizardStyles } from '@/styles/wizardStyles';

const { Text } = Typography;

export interface FormFieldWrapperProps {
  /** Field label text */
  label: string;

  /** Whether the field is required (shows red asterisk) */
  required?: boolean;

  /** Error message to display below the field */
  error?: string;

  /** Help text to display below the field (only shown when no error) */
  help?: string;

  /** The form input component */
  children: React.ReactNode;

  /** Additional class name for the wrapper */
  className?: string;
}

/**
 * Form Field Wrapper Component
 *
 * Provides consistent label, required indicator, and error message display
 * for all form fields.
 */
export const FormFieldWrapper: React.FC<FormFieldWrapperProps> = ({
  label,
  required = false,
  error,
  help,
  children,
  className,
}) => {
  const styles = useWizardStyles();

  return (
    <div className={className}>
      {/* Label with required indicator */}
      <Text style={styles.fieldLabel}>
        {required && <span style={styles.requiredIndicator}>*</span>}
        {label}
      </Text>

      {/* Form input */}
      {children}

      {/* Error message or help text */}
      {error ? (
        <Text style={styles.errorText}>{error}</Text>
      ) : help ? (
        <Text style={styles.helpText}>{help}</Text>
      ) : null}
    </div>
  );
};

export default FormFieldWrapper;
